import { loginAPI, getUserInfo as getUserInfoAPI, updateUserInfo, logoutAPI, getLoan, getHomePageData } from '@/utils/request'
import { getStorage, setStorage, removeStorage } from '@/utils/storage'
import { deepClone, maskPhone, recordError } from '@/utils/security'

export default {
  namespaced: true,
  
  state: () => ({
    userInfo: getStorage('userInfos', null),
    token: getStorage('token', ''),
    isLogin: !!getStorage('token', ''),
    phoneNumber: ''
  }),
  
  mutations: {
    SET_USER_INFO(state, userInfo) {
      const clonedUserInfo = deepClone(userInfo)
      state.userInfo = clonedUserInfo
      if (clonedUserInfo) {
        setStorage('userInfos', clonedUserInfo, { ttl: 7 * 24 * 60 * 60 * 1000 })
      }
    },
    SET_TOKEN(state, token) {
      state.token = token
      setStorage('token', token, { ttl: 7 * 24 * 60 * 60 * 1000 })
    },
    SET_LOGIN_STATUS(state, status) {
      state.isLogin = status
    },
    SET_PHONE_NUMBER(state, phone) {
      state.phoneNumber = phone
    },
    CLEAR_USER(state) {
      state.userInfo = null
      state.token = ''
      state.isLogin = false
      state.phoneNumber = ''
      removeStorage('token')
      removeStorage('userInfos')
      removeStorage('refreshToken')
    }
  },
  
  actions: {
    // 用户登录
    async login({ commit, dispatch }, loginData) {
      try {
        const response = await loginAPI(loginData)
        
        if (response.code === 1) {
          // 尝试多种方式获取token
          let token = response.data?.token
          if (!token) {
            token = response.token
          }
          if (!token) {
            token = response.data
          }
          
          const userInfo = response.data?.userinfo || response.userinfo
          
          if (token) {
            commit('SET_TOKEN', token)
          }
          if (userInfo) {
            commit('SET_USER_INFO', userInfo)
            if (userInfo.mobile) {
              commit('SET_PHONE_NUMBER', userInfo.mobile)
            }
          }
          commit('SET_LOGIN_STATUS', true)
          
          return {
            success: true,
            message: response.msg || '登录成功',
            data: response.data
          }
        } else {
          commit('CLEAR_USER')
          return {
            success: false,
            message: response.msg || '登录失败',
            code: response.code
          }
        }
      } catch (error) {
        recordError(error, 'user/login')
        commit('CLEAR_USER')
        let errorMessage = '网络异常，请重试'
        
        if (error.response && error.response.data) {
          errorMessage = error.response.data.message || error.response.data.msg || errorMessage
        } else if (error.msg) {
          errorMessage = error.msg
        } else if (error.message) {
          errorMessage = error.message
        }
        
        return {
          success: false,
          message: errorMessage,
          code: error.code || (error.response && error.response.status) || -1
        }
      }
    },

    // 获取收款信息
    async getLoan({ commit, state }) {
      try {
        const response = await getLoan(state.token)
        return response.data
      } catch (error) {
        recordError(error, 'user/getLoan')
        return Promise.reject(error)
      }
    },

    // 获取用户信息
    async getUserInfo({ commit, state }) {
      try {
        const response = await getUserInfoAPI()
        if (response.code === 1) {
          commit('SET_USER_INFO', response.data.userinfo)
          return response
        }
        return Promise.reject(response)
      } catch (error) {
        recordError(error, 'user/getUserInfo')
        return Promise.reject(error)
      }
    },

    // 自动登录（token存在时）
    async autoLogin({ commit, state, dispatch }) {
      if (!state.token) {
        return { success: false, message: '未找到登录信息' }
      }
      
      if (state.userInfo) {
        commit('SET_LOGIN_STATUS', true)
        return { success: true, message: '自动登录成功' }
      }
      
      try {
        const userResponse = await getUserInfoAPI()
        
        if (userResponse.code === 1) {
          commit('SET_LOGIN_STATUS', true)
          commit('SET_USER_INFO', userResponse.data.userinfo)
          return { success: true, message: '自动登录成功' }
        } else {
          commit('CLEAR_USER')
          return { success: false, message: userResponse.msg || '登录失败' }
        }
      } catch (error) {
        recordError(error, 'user/autoLogin')
        commit('CLEAR_USER')
        return { success: false, message: '登录已过期，请重新登录' }
      }
    },

    // 用户退出
    async logout({ commit, state }) {
      try {
        const response = await logoutAPI()
        if (response.code === 1) {
          commit('CLEAR_USER')
          return { success: true, message: response.msg || '退出成功' }
        } else {
          commit('CLEAR_USER')
          return { success: true, message: response.msg || '退出成功' }
        }
      } catch (error) {
        recordError(error, 'user/logout')
        commit('CLEAR_USER')
        return { success: true, message: '退出成功' }
      }
    },

    // 获取主页数据（独立于登录返回的用户数据）
    async getHomePageData({ commit, state }) {
      try {
        const response = await getHomePageData()
        if (response.code === 1) {
          return { success: true, data: response.data }
        }
        return { success: false, message: response.msg || '获取主页数据失败' }
      } catch (error) {
        recordError(error, 'user/getHomePageData')
        return { success: false, message: '网络异常，请重试' }
      }
    }
  },
  
  getters: {
    // 获取用户手机号（脱敏处理）
    maskedPhone: (state) => {
      if (!state.phoneNumber) return ''
      return maskPhone(state.phoneNumber)
    },
    
    // 判断是否已登录
    isAuthenticated: (state) => {
      return !!state.token
    }
  }
}
